import { useEffect, useMemo, useRef, useState } from 'react';
import { useEventCallback } from '@eas/common-web';
import { TreeObject } from '@models';
import { fetchLocalTree } from './local-tree-api';
import { LocalTreeBuildResult } from './local-tree-types';
import { buildLocalTree, getVisibleTree } from './local-tree-utils';

const EMPTY_TREE: LocalTreeBuildResult = {
  roots: [],
  nodeMap: new Map(),
};

export function shouldLoadLocalTree({
  hasSelectedItems,
  loaded,
  loading,
  popupOpen,
}: {
  hasSelectedItems: boolean;
  loaded: boolean;
  loading: boolean;
  popupOpen: boolean;
}) {
  return (popupOpen || hasSelectedItems) && !loaded && !loading;
}

export function useLocalTreeSource<OPTION extends TreeObject>({
  hasSelectedItems,
  popupOpen,
  searchQuery,
  url,
}: {
  hasSelectedItems: boolean;
  popupOpen: boolean;
  searchQuery: string;
  url: string;
}) {
  const [tree, setTree] = useState<LocalTreeBuildResult<OPTION>>(
    EMPTY_TREE as LocalTreeBuildResult<OPTION>
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const loadedRef = useRef(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    loadedRef.current = false;
    loadingRef.current = false;
    setTree(EMPTY_TREE as LocalTreeBuildResult<OPTION>);
    setError(null);
    setExpandedIds(new Set());
    setLoading(false);
  }, [url]);

  useEffect(() => {
    let active = true;

    async function load() {
      if (
        !shouldLoadLocalTree({
          hasSelectedItems,
          popupOpen,
          loaded: loadedRef.current,
          loading: loadingRef.current,
        })
      ) {
        return;
      }

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await fetchLocalTree<OPTION>(url);

        if (!active) {
          return;
        }

        loadedRef.current = true;
        setTree(buildLocalTree(response.items));
      } catch (err: any) {
        if (!active) {
          return;
        }

        setError(err?.message ?? 'Nepodarilo se nacist strom.');
      } finally {
        if (active) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [hasSelectedItems, popupOpen, url]);

  const toggleExpanded = useEventCallback((id: string) => {
    setExpandedIds((currentExpandedIds) => {
      const nextExpandedIds = new Set(currentExpandedIds);

      if (nextExpandedIds.has(id)) {
        nextExpandedIds.delete(id);
      } else {
        nextExpandedIds.add(id);
      }

      return nextExpandedIds;
    });
  });

  const visibleRoots = useMemo(
    () =>
      getVisibleTree({
        roots: tree.roots,
        nodeMap: tree.nodeMap,
        expandedIds,
        searchQuery,
      }),
    [expandedIds, searchQuery, tree]
  );

  return {
    error,
    isExpanded: (id: string) => expandedIds.has(id),
    loading,
    nodeMap: tree.nodeMap,
    toggleExpanded,
    visibleRoots,
  };
}
